import { aggregateCategoryForums } from './categoryAggregates';
import { getForumGrowthPercent } from './forumGrowth';
import { calculateForumScore } from './forumScore';
import type { ForumBase } from '../types/forum';

export type SummaryInsights = {
  periodLabel: string;
  champion: (ForumBase & { growthPercent: number }) | null;
  topRiser: (ForumBase & { growthPercent: number }) | null;
  worstDecline: (ForumBase & { growthPercent: number }) | null;
  decliningCategory: { name: string; growthPercent: number } | null;
  growingCategory: { name: string; growthPercent: number } | null;
  totalForums: number;
  totalMembers: number;
  membersGrowthPct: string | null;
  rising: Array<ForumBase & { growthPercent: number }>;
  declining: Array<ForumBase & { growthPercent: number }>;
};

function growthPct(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return ((current - previous) / previous) * 100;
}

export function buildSummaryInsights(forums: ForumBase[], isWeekly: boolean): SummaryInsights {
  const active = forums.filter((f) => f.is_active !== false);
  const periodLabel = isWeekly ? 'Bu hafta' : 'Bu ay';

  const withGrowth = active
    .map((f) => {
      const g = getForumGrowthPercent(f, 'members');
      return g !== null ? { ...f, growthPercent: g } : null;
    })
    .filter((x): x is ForumBase & { growthPercent: number } => x !== null);

  const rising = [...withGrowth].filter((f) => f.growthPercent > 0).sort((a, b) => b.growthPercent - a.growthPercent);
  const declining = [...withGrowth].filter((f) => f.growthPercent < 0).sort((a, b) => a.growthPercent - b.growthPercent);

  const champion = [...active]
    .sort(
      (a, b) =>
        calculateForumScore(b.topics_count, b.posts_count, b.members_count, b.founded_year) -
        calculateForumScore(a.topics_count, a.posts_count, a.members_count, a.founded_year)
    )[0];

  const championWithGrowth = champion
    ? (() => {
        const g = getForumGrowthPercent(champion, 'members');
        return g !== null ? { ...champion, growthPercent: g } : null;
      })()
    : null;

  const byCategory = new Map<string, ForumBase[]>();
  for (const f of active) {
    if (!f.category) continue;
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }

  let decliningCategory: SummaryInsights['decliningCategory'] = null;
  let growingCategory: SummaryInsights['growingCategory'] = null;

  for (const [name, list] of byCategory) {
    if (list.length < 2) continue;
    const agg = aggregateCategoryForums(list);
    const pct = growthPct(agg.totalMembers, agg.previousTotalMembers);
    if (pct === null) continue;
    if (!decliningCategory || pct < decliningCategory.growthPercent) {
      decliningCategory = { name, growthPercent: pct };
    }
    if (!growingCategory || pct > growingCategory.growthPercent) {
      growingCategory = { name, growthPercent: pct };
    }
  }

  const totalMembers = active.reduce((s, f) => s + f.members_count, 0);
  const prevMembers = active.reduce((s, f) => s + (f.previous_month_members_count ?? 0), 0);
  const membersGrowth = growthPct(totalMembers, prevMembers);

  return {
    periodLabel,
    champion: championWithGrowth,
    topRiser: rising[0] ?? null,
    worstDecline: declining[0] ?? null,
    decliningCategory: decliningCategory && decliningCategory.growthPercent < 0 ? decliningCategory : null,
    growingCategory: growingCategory && growingCategory.growthPercent > 0 ? growingCategory : null,
    totalForums: active.length,
    totalMembers,
    membersGrowthPct:
      membersGrowth !== null
        ? `${membersGrowth > 0 ? '+' : ''}${membersGrowth.toFixed(1)}%`
        : null,
    rising: rising.slice(0, 10),
    declining: declining.slice(0, 5),
  };
}
